import type { App } from "@open-booking/server";
import type { PublicPriceCalculationPayload, SelfServiceBookingPayload, SignContractPayload } from "@open-booking/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type EdenCall, edenBaseQuery } from "@/src/common/lib/eden-base-query";

type GetPublicPropertyResponse = App["~Routes"]["public"]["properties"][":slug"]["get"]["response"]["200"];
type GetPublicAvailabilityResponse = App["~Routes"]["public"]["properties"][":slug"]["availability"]["get"]["response"]["200"];
type CalculatePriceResponse = App["~Routes"]["public"]["properties"][":slug"]["price"]["post"]["response"]["200"];
type CreateSelfServiceBookingResponse = App["~Routes"]["public"]["properties"][":slug"]["bookings"]["post"]["response"]["200"];
type GetContractToSignResponse = App["~Routes"]["contracts"]["sign"][":token"]["get"]["response"]["200"];
type SignContractResponse = App["~Routes"]["contracts"]["sign"][":token"]["post"]["response"]["200"];

export const publicApi = createApi({
	reducerPath: "publicApi",
	baseQuery: edenBaseQuery(),
	tagTypes: ["Contract", "Availability"],
	endpoints: (builder) => ({
		getPublicProperty: builder.query<GetPublicPropertyResponse, string>({
			query:
				(slug): EdenCall<GetPublicPropertyResponse> =>
				(client) =>
					client.public.properties({ slug }).get(),
		}),
		getPublicAvailability: builder.query<GetPublicAvailabilityResponse, { slug: string; from: string; to: string }>({
			query:
				({ slug, from, to }): EdenCall<GetPublicAvailabilityResponse> =>
				(client) =>
					client.public.properties({ slug }).availability.get({ query: { from, to } }),
			providesTags: (_res, _err, { slug }) => [{ type: "Availability", id: slug }],
		}),
		calculatePrice: builder.mutation<CalculatePriceResponse, { slug: string; data: PublicPriceCalculationPayload }>({
			query:
				({ slug, data }): EdenCall<CalculatePriceResponse> =>
				(client) =>
					client.public.properties({ slug }).price.post(data),
		}),
		createSelfServiceBooking: builder.mutation<CreateSelfServiceBookingResponse, { slug: string; data: SelfServiceBookingPayload }>({
			query:
				({ slug, data }): EdenCall<CreateSelfServiceBookingResponse> =>
				(client) =>
					client.public.properties({ slug }).bookings.post(data),
			invalidatesTags: (_res, _err, { slug }) => [{ type: "Availability", id: slug }],
		}),
		getContractToSign: builder.query<GetContractToSignResponse, string>({
			query:
				(token): EdenCall<GetContractToSignResponse> =>
				(client) =>
					client.contracts.sign({ token }).get(),
			providesTags: (_res, _err, token) => [{ type: "Contract", id: token }],
		}),
		signContract: builder.mutation<SignContractResponse, { token: string; data: SignContractPayload }>({
			query:
				({ token, data }): EdenCall<SignContractResponse> =>
				(client) =>
					client.contracts.sign({ token }).post(data),
			invalidatesTags: (_res, _err, { token }) => [{ type: "Contract", id: token }],
		}),
	}),
});

export const {
	useGetPublicPropertyQuery,
	useGetPublicAvailabilityQuery,
	useCalculatePriceMutation,
	useCreateSelfServiceBookingMutation,
	useGetContractToSignQuery,
	useSignContractMutation,
} = publicApi;
